import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, Mail, Clock, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const ICON_BY_TYPE = {
  priority_increased: { emoji: "🔺", color: "#C65D47" },
  task_reassigned: { emoji: "↪", color: "#D99A5B" },
  task_completed: { emoji: "✓", color: "#82937E" },
  task_reopened: { emoji: "↺", color: "#2C402E" },
  cross_collab_created: { emoji: "⇄", color: "#2C402E" },
  task_created: { emoji: "+", color: "#5C635B" },
  digest_sent: { emoji: "✉", color: "#5C635B" },
  registration_pending: { emoji: "👤", color: "#2C402E" },
};

function navigateForType(navigate, n) {
  if (n.type === "registration_pending") {
    navigate("/zahtevi");
    return true;
  }
  if (n.task_id) {
    navigate(`/taskovi?open=${n.task_id}`);
    return true;
  }
  return false;
}

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "pre nekoliko sekundi";
  if (s < 3600) return `pre ${Math.floor(s / 60)} min`;
  if (s < 86400) return `pre ${Math.floor(s / 3600)}h`;
  return `pre ${Math.floor(s / 86400)}d`;
}

function snoozeOptions() {
  const now = new Date();
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  const tomorrow8 = new Date(now); tomorrow8.setDate(tomorrow8.getDate() + 1); tomorrow8.setHours(8, 0, 0, 0);
  const inThreeH = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); nextWeek.setHours(8, 0, 0, 0);
  return [
    { label: "1 sat", iso: inOneHour.toISOString() },
    { label: "3 sata", iso: inThreeH.toISOString() },
    { label: "Sutra u 08:00", iso: tomorrow8.toISOString() },
    { label: "Kroz nedelju u 08:00", iso: nextWeek.toISOString() },
  ];
}

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const handleItemClick = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    }
    const navigated = navigateForType(navigate, n);
    if (navigated) {
      setOpen(false);
      loadCount(); loadItems();
    }
  };

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* silent */ }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 }});
      setItems(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, 30000);
    return () => clearInterval(iv);
  }, [loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const markOne = async (id, e) => {
    e?.stopPropagation();
    await api.patch(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    loadCount();
  };
  const del = async (id, e) => {
    e?.stopPropagation();
    await api.delete(`/notifications/${id}`);
    setItems((prev) => prev.filter((n) => n.id !== id));
    loadCount();
  };
  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    loadCount();
  };
  const snooze = async (id, iso, label) => {
    try {
      await api.post(`/notifications/${id}/snooze`, { until: iso });
      setItems((prev) => prev.filter((n) => n.id !== id));
      loadCount();
      toast.success(`Odloženo: ${label}`);
    } catch { toast.error("Nije uspelo odlaganje"); }
  };
  const sendDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/send-digest");
      if (data.ok) toast.success("Jutarnji brief poslat na tvoj email");
      else toast.error(data.reason || "Nije poslato");
      loadItems(); loadCount();
    } catch { toast.error("Greška pri slanju digest-a"); }
    setBusy(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 grid place-items-center rounded-md border border-line bg-white hover:bg-surface-secondary transition-colors"
          aria-label="Notifikacije"
        >
          <Bell className="w-4 h-4 text-ink" />
          {count > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-terracotta text-white text-[10px] font-bold grid place-items-center px-1"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] p-0 bg-white border-line"
        align="end"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <div className="font-heading font-semibold">Notifikacije</div>
            <div className="text-xs text-ink-muted">{count > 0 ? `${count} nepročitanih` : "Sve pročitano"}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={sendDigest}
              disabled={busy}
              data-testid="send-digest-btn"
              className="text-xs text-forest hover:underline inline-flex items-center gap-1 px-2 py-1"
              title="Pošalji jutarnji brief na email sada"
            >
              <Mail className="w-3 h-3" /> Digest
            </button>
            <button
              onClick={markAll}
              data-testid="mark-all-read-btn"
              className="text-xs text-forest hover:underline inline-flex items-center gap-1 px-2 py-1"
            >
              <Check className="w-3 h-3" /> Sve pročitano
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-muted">Nema notifikacija.</div>
          ) : (
            items.map((n) => {
              const icon = ICON_BY_TYPE[n.type] || ICON_BY_TYPE.task_created;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  onClick={() => handleItemClick(n)}
                  className={`group px-4 py-3 border-b border-line last:border-b-0 flex items-start gap-3 cursor-pointer hover:bg-surface-secondary ${n.read ? "opacity-60" : "bg-surface-secondary/40"}`}
                >
                  <div
                    className="mt-0.5 w-7 h-7 shrink-0 rounded-md grid place-items-center text-sm font-bold"
                    style={{ background: `${icon.color}15`, color: icon.color }}
                  >
                    {icon.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink-muted">{n.title} · {timeAgo(n.created_at)}</div>
                    <div className={`text-sm ${n.read ? "text-ink-muted" : "text-ink font-medium"}`}>{n.body}</div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`notification-snooze-${n.id}`}
                          className="text-ink-muted hover:text-forest inline-flex items-center"
                          title="Odloži"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          <ChevronDown className="w-3 h-3 -ml-0.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-ink-muted">Odloži do</div>
                        {snoozeOptions().map((o) => (
                          <DropdownMenuItem
                            key={o.label}
                            data-testid={`snooze-${n.id}-${o.label}`}
                            onSelect={(e) => { e.preventDefault(); snooze(n.id, o.iso, o.label); }}
                            className="cursor-pointer"
                          >
                            {o.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {!n.read && (
                      <button
                        onClick={(e) => markOne(n.id, e)}
                        data-testid={`notification-read-${n.id}`}
                        className="text-ink-muted hover:text-forest"
                        title="Označi kao pročitano"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => del(n.id, e)}
                      data-testid={`notification-delete-${n.id}`}
                      className="text-ink-muted hover:text-terracotta"
                      title="Obriši"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
